// Rankings Portal audio — 13 distinct SFX + the legacy chime.
//
// Files live in /public/audio/ (new pack) and /public/sounds/ (legacy chime).
// One sample per event so operators across the warehouse can identify what
// happened by ear without looking at the board.
//
// Pool pattern: 3 Audio() instances per SFX. On play, cycle the pool so
// rapid-fire triggers overlap cleanly instead of cutting each other off
// (a single Audio() re-plays by resetting currentTime, which clips the
// previous playthrough). 3 slots gives ~200-400ms overlap tolerance.
//
// `?mute=1` on the display URL is honored at the caller layer; play() is
// wrapped in try/catch so a missing file never blows up the polling loop.

// Event → sound file. One distinct sample per event.
const SFX = {
    tick:        '/audio/floraphonic-arcade-ui-3-229508.mp3',                       // score-up
    overtake:    '/audio/floraphonic-arcade-ui-13-229512.mp3',                      // rank +1 ("on the move") — TODO: swap to soundreality-race-care-151963 when the file is available
    bigOvertake: '/audio/floraphonic-arcade-ui-16-229516.mp3',                      // rank +3 ("+N POSITIONS") — TODO: swap to nico0825-power-up-strike-446145 when available
    stormingUp:  '/audio/freesound_community-power-on-39172.mp3',                   // rank +6 ("STORMING UP")
    podium:      '/audio/podium.mp3',                                               // into top-3, fades out at end
    onFire:      '/audio/floraphonic-fire-torch-whoosh-5-190301.mp3',               // streak → ON FIRE
    blazing:     '/audio/dragon-studio-fireworks-11-419030.mp3',                    // streak → BLAZING
    levelUp:     '/audio/universfield-level-up-05-326133.mp3',                      // LV badge crossed
    halfMachine: '/audio/floraphonic-cute-level-up-2-189851.mp3',                   // HALF MACHINE title earned
    theMachine:  '/audio/floraphonic-cute-level-up-3-189853.mp3',                   // THE MACHINE title earned
    maxMachine:  '/audio/tithuh-level-up-03-523644.mp3',                            // MAX MACHINE title earned
    megaMachine: '/audio/tithuh-level-up-523624.mp3',                               // MEGA MACHINE title earned
    achievement: '/audio/tithuh-level-up-0-523643.mp3',                             // trophy popup
    milestone:   '/audio/floraphonic-classic-game-action-positive-30-224562.mp3',   // team milestone
    rivals:      '/audio/floraphonic-cute-level-up-1-189852.mp3',                   // Rivals trophy (formerly ON FIRE)
    battle:      '/audio/juanfranciscomultimedi-epic-battle-sound-9414.mp3',        // BATTLE cut-in — 13s epic battle horn
};

// Per-event volume (0-1). Ticks are quiet because they fire often. Machine
// tier upgrades and milestones are louder because they're rare + celebratory.
const VOLUME = {
    tick:        0.20,
    overtake:    0.45,
    bigOvertake: 0.60,
    stormingUp:  0.70,
    podium:      0.75,
    onFire:      0.55,
    blazing:     0.70,
    levelUp:     0.50,
    halfMachine: 0.55,
    theMachine:  0.65,
    maxMachine:  0.75,
    megaMachine: 0.85,
    achievement: 0.70,
    milestone:   0.80,
    rivals:      0.65,
    battle:      0.85,
    chime:       0.60,
};

const POOL_SIZE = 3;
const pools = {};

function pool(key, url) {
    if (pools[key]) return pools[key];
    const els = Array.from({ length: POOL_SIZE }, () => {
        const a = new Audio(url);
        a.volume = VOLUME[key] ?? 0.5;
        a.preload = 'auto';
        return a;
    });
    pools[key] = { els, idx: 0 };
    return pools[key];
}

function playFrom(key, url) {
    try {
        const p = pool(key, url);
        const el = p.els[p.idx];
        p.idx = (p.idx + 1) % p.els.length;
        el.currentTime = 0;
        el.play().catch(() => {});
    } catch (_) {}
}

// Same as playFrom but fades the audio to silence over `fadeMs` at the end.
// Uses setInterval on the shared audio element — for one-shots this is fine.
function playFromFading(key, url, fadeMs = 700) {
    try {
        const p = pool(key, url);
        const el = p.els[p.idx];
        p.idx = (p.idx + 1) % p.els.length;
        el.currentTime = 0;
        const baseVol = VOLUME[key] ?? 0.5;
        el.volume = baseVol;
        el.play().catch(() => {});
        // Kick off the fade at (duration - fadeMs). If metadata isn't loaded
        // yet, wait for it — otherwise we don't know duration.
        const startFade = () => {
            const durSec = el.duration || 0;
            if (!durSec) return;
            const remaining = durSec - el.currentTime;
            const fadeStartInMs = Math.max(0, (remaining * 1000) - fadeMs);
            setTimeout(() => {
                const steps = 20;
                const step = fadeMs / steps;
                const decrement = baseVol / steps;
                let i = 0;
                const timer = setInterval(() => {
                    i++;
                    el.volume = Math.max(0, baseVol - decrement * i);
                    if (i >= steps) {
                        clearInterval(timer);
                        el.pause();
                        // Reset for next play
                        el.currentTime = 0;
                        el.volume = baseVol;
                    }
                }, step);
            }, fadeStartInMs);
        };
        if (el.readyState >= 1) startFade();
        else el.addEventListener('loadedmetadata', startFade, { once: true });
    } catch (_) {}
}

// One function per event.
export function playTick()        { playFrom('tick',        SFX.tick);        }
export function playOvertake()    { playFrom('overtake',    SFX.overtake);    }
export function playBigOvertake() { playFrom('bigOvertake', SFX.bigOvertake); }
export function playStormingUp()  { playFrom('stormingUp',  SFX.stormingUp);  }
export function playPodium()      { playFromFading('podium', SFX.podium, 700); }
export function playOnFire()      { playFrom('onFire',      SFX.onFire);      }
export function playBlazing()     { playFrom('blazing',     SFX.blazing);     }
export function playLevelUp()     { playFrom('levelUp',     SFX.levelUp);     }
export function playHalfMachine() { playFrom('halfMachine', SFX.halfMachine); }
export function playTheMachine()  { playFrom('theMachine',  SFX.theMachine);  }
export function playMaxMachine()  { playFrom('maxMachine',  SFX.maxMachine);  }
export function playMegaMachine() { playFrom('megaMachine', SFX.megaMachine); }
export function playAchievement() { playFrom('achievement', SFX.achievement); }
export function playMilestone()   { playFrom('milestone',   SFX.milestone);   }
export function playRivals()      { playFrom('rivals',      SFX.rivals);      }
export function playBattle()      { playFrom('battle',      SFX.battle);      }

// Convenience: play the right streak sound for the level index (1 = HOT is
// still synth sizzle; 2 = ON FIRE = onFire.mp3; 3 = BLAZING = blazing.mp3).
export function playStreakForLevel(idx) {
    if (idx >= 3) playBlazing();
    else if (idx >= 2) playOnFire();
}

// Convenience: play the right Machine sound for the tier crossed.
// tier is 1..6 for HALF / THE / MAX / MEGA / ULTRA SUPER / GOD MODE.
// The top two rungs (5, 6) layer megaMachine + milestone + chime for
// a fuller "unlocked" feel since they're mythical events.
export function playMachineTier(tier) {
    if (tier === 6) {                    // GOD MODE — every sound at once
        playMegaMachine();
        playMilestone();
        setTimeout(playChime, 200);
    } else if (tier === 5) {             // ULTRA SUPER — MEGA + milestone
        playMegaMachine();
        setTimeout(playMilestone, 250);
    } else if (tier === 4) {
        playMegaMachine();
    } else if (tier === 3) {
        playMaxMachine();
    } else if (tier === 2) {
        playTheMachine();
    } else if (tier === 1) {
        playHalfMachine();
    }
}

// Legacy chime — the first-place bell.
export const CHIME_URL = '/sounds/first_place.mp3';
let chimeEl = null;
export function playChime() {
    try {
        if (!chimeEl) { chimeEl = new Audio(CHIME_URL); chimeEl.volume = VOLUME.chime; }
        chimeEl.currentTime = 0;
        chimeEl.play().catch(() => {});
    } catch (_) {}
}
